// 051 (e) Morbilidad, Intervenciones y Near miss.
//  - Morbilidad: por grupo un Observation con la cabecera no/si del papel (valueBoolean, "No" explícito incluido) y un component por ítem
//    contestado; cada ítem en "Sí" además es una `Condition` confirmada (código propio de morbilidad perinatal: el papel no trae CIE-10 ahí).
//    Con la cabecera en "No" los ítems no se guardan (sin "Sí" huérfano).
//  - Intervenciones: Observation con los no/si/s-d ("s/d" = dataAbsentReason unknown) y una `Procedure` por cada procedimiento realizado.
//  - Near miss: el padre de todo es "Variables para identificar casos de near miss"; en "No" no se guarda ningún criterio.
import type { CodeableConcept } from '@medplum/fhirtypes';
import { leerSiNo } from '../camposSimplesDatos';
import {
  COMPLICACIONES_OBSTETRICAS_OPCIONES,
  HEMORRAGIA_TIPOS_OPCIONES,
  HEMORRAGIA_TRIMESTRE_OPCIONES,
  INFECCIONES_051_OPCIONES,
  INTERVENCIONES_051_OPCIONES,
  OTROS_TRASTORNOS_051_OPCIONES,
  PROCEDIMIENTOS_HEMORRAGIA_OPCIONES,
  TRASTORNOS_HIPERTENSIVOS_OPCIONES,
  TRASTORNOS_METABOLICOS_DIABETES_OPCIONES,
  TRASTORNOS_TIROIDEOS_OPCIONES,
  type EstadoIntervenciones051,
  type EstadoMorbilidad051,
} from '../bloques051PartoDatos';
import {
  NEAR_MISS_CRITERIOS_CLINICOS_OPCIONES,
  NEAR_MISS_INTERVENCIONES_SIMPLES_OPCIONES,
  NEAR_MISS_LABORATORIO_ITEMS_OPCIONES,
  type EstadoNearMiss051,
} from '../bloques051PartogramaDatos';
import {
  cNumero,
  cSiNo,
  cSiNoSd,
  cTexto,
  crearCondicion,
  local,
  Seccion051,
  SISTEMA_051_MORBILIDAD,
  U,
  valorOpcion,
  type Comp,
  type Contexto051,
  type ProcedimientosCreados,
} from './base';

const M = 'formularios.f051.morbilidad';
const I = 'formularios.f051.intervenciones';
const N = 'formularios.f051.nearMiss';

const codigoMorbilidad = (clave: string, display: string, texto?: string): CodeableConcept => ({
  coding: [{ system: SISTEMA_051_MORBILIDAD, code: clave, display }],
  text: texto?.trim() || display,
});

export function agregarMorbilidad(ctx: Contexto051, e: EstadoMorbilidad051): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('morbilidad', 'Morbilidad'));

  /** Cabecera no/si + ítems no/si; cada ítem en Sí crea su Condition. `extra`: componentes ligados a un ítem (el "cuál"). */
  const grupo = (clave: string, prefijo: string, opciones: readonly string[], presente: string, valores: Readonly<Record<string, string>>, cual?: { readonly item: string; readonly texto: string; readonly rotulo: string }): void => {
    const titulo = t(`${prefijo}.titulo`);
    const abierto = presente !== 'no';
    const itemSi = (c: string): boolean => abierto && leerSiNo(valores[c]) === 'si';
    const comps: Comp[] = abierto ? opciones.map((c) => cSiNo(local(`${clave}-${c}`, t(`${prefijo}.opciones.${c}`)), leerSiNo(valores[c]))) : [];
    if (cual && itemSi(cual.item)) comps.push(cTexto(local(`${clave}-${cual.item}-cual`, cual.rotulo), cual.texto));
    s.grupo(local(clave, titulo), comps, presente === 'si' ? { valueBoolean: true } : presente === 'no' ? { valueBoolean: false } : {});
    for (const c of opciones) {
      if (!itemSi(c)) continue;
      const display = t(`${prefijo}.opciones.${c}`);
      crearCondicion(ctx, codigoMorbilidad(`${clave}-${c}`, display, cual && cual.item === c ? cual.texto : undefined));
    }
  };

  grupo('trastornos-hipertensivos', `${M}.trastornosHipertensivos`, TRASTORNOS_HIPERTENSIVOS_OPCIONES, e.trastornosHipertensivosPresente, e.trastornosHipertensivos);
  grupo('infecciones', `${M}.infecciones`, INFECCIONES_051_OPCIONES, e.infeccionesPresente, e.infecciones, { item: 'otraInfeccion', texto: e.otraInfeccionCual, rotulo: t(`${M}.otraInfeccionCual`) });

  s.grupo(
    local('hemorragia-trimestres', t(`${M}.hemorragiaTrimestre`)),
    HEMORRAGIA_TRIMESTRE_OPCIONES.map((tr) => cSiNo(local(`hemorragia-trimestre-${tr}`, t(`${M}.hemorragiaTrimestreOpciones.${tr}`)), leerSiNo(e.hemorragiaTrimestres[tr])))
  );
  // Los tipos NO cuelgan de los trimestres (la columna derecha del papel es del parto/puerperio).
  grupo('hemorragia-tipos', `${M}.hemorragiaTipos`, HEMORRAGIA_TIPOS_OPCIONES, '', e.hemorragiaTipos);

  if (e.inmunoglobulinaAntiRhD) {
    s.grupo(local('inmunoglobulina-anti-rh-d', t(`${M}.inmunoglobulinaAntiRhD`)), [], { valueCodeableConcept: valorOpcion(e.inmunoglobulinaAntiRhD, t(`${M}.inmunoglobulinaAntiRhDOpciones.${e.inmunoglobulinaAntiRhD}`)) });
  }

  s.grupo(local('trastornos-metabolicos', t(`${M}.trastornosMetabolicos`)), [], e.trastornosMetabolicosPresente === 'si' ? { valueBoolean: true } : e.trastornosMetabolicosPresente === 'no' ? { valueBoolean: false } : {});
  if (e.trastornosMetabolicosPresente !== 'no') {
    grupo('metabolico-diabetes', `${M}.trastornosMetabolicosDiabetes`, TRASTORNOS_METABOLICOS_DIABETES_OPCIONES, e.diabetesPresente, e.trastornosMetabolicosDiabetes);
    grupo('metabolico-tiroideo', `${M}.trastornosTiroideos`, TRASTORNOS_TIROIDEOS_OPCIONES, e.tiroideosPresente, e.trastornosTiroideos, { item: 'otro', texto: e.otroTrastornoMetabolicoCual, rotulo: t(`${M}.otroTrastornoMetabolicoCual`) });
  }

  grupo('otros-trastornos', `${M}.otrosTrastornos`, OTROS_TRASTORNOS_051_OPCIONES, e.otrosTrastornosPresente, e.otrosTrastornos);
  grupo('complicaciones-obstetricas', `${M}.complicacionesObstetricas`, COMPLICACIONES_OBSTETRICAS_OPCIONES, e.complicacionesPresente, e.complicacionesObstetricas, { item: 'otros', texto: e.complicacionesOtrosCual, rotulo: t(`${M}.complicacionesOtrosCual`) });

  s.cerrar();
}

export function agregarIntervenciones(ctx: Contexto051, e: EstadoIntervenciones051, procedimientos: ProcedimientosCreados): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('intervenciones', 'Intervenciones'));
  const v = (c: string): string => e.intervenciones[c] ?? '';
  const dependientes: Readonly<Record<string, Comp>> = {
    uterotonicosTtoHemorragia: cTexto(local('uterotonicos-cual', t(`${I}.uterotonicosCual`)), e.uterotonicosCual),
    administracionHemoderivados: cTexto(local('hemoderivados-volumenes', t(`${I}.hemoderivadosVolumenes`)), e.hemoderivadosVolumenes),
    ingresoUci7Dias: cNumero(local('ingreso-uci-dias', t(`${I}.ingresoUciDias`)), e.ingresoUciDias, U.dias),
    usoAtbIv: cTexto(local('uso-atb-iv-cual', t(`${I}.usoAtbIvCual`)), e.usoAtbIvCual),
  };
  s.grupo(
    local('intervenciones-obstetricas', 'Intervenciones obstétricas'),
    INTERVENCIONES_051_OPCIONES.flatMap((c) => [cSiNoSd(local(c, t(`${I}.${c}`)), v(c)), v(c) === 'si' ? dependientes[c] : undefined])
  );
  s.grupo(
    local('procedimientos-hemorragia', t(`${I}.procedimientosHemorragia.titulo`)),
    PROCEDIMIENTOS_HEMORRAGIA_OPCIONES.map((c) => cSiNoSd(local(c, t(`${I}.procedimientosHemorragia.opciones.${c}`)), e.procedimientosHemorragia[c] ?? ''))
  );
  s.cerrar();

  // Procedure solo de lo realizado ("Sí"). El ingreso a UCI y los uterotónicos/antibióticos quedan como Observation (no son procedimientos).
  if (v('alumbramientoManual') === 'si') procedimientos.crear(ctx, 'alumbramiento-manual', t(`${I}.alumbramientoManual`));
  if (v('viaVenosaCentral') === 'si') procedimientos.crear(ctx, 'via-venosa-central', t(`${I}.viaVenosaCentral`));
  if (v('laparotomia') === 'si') procedimientos.crear(ctx, 'laparotomia', t(`${I}.laparotomia`));
  if (v('administracionHemoderivados') === 'si') procedimientos.crear(ctx, 'transfusion-hemoderivados', t(`${I}.administracionHemoderivados`), undefined, e.hemoderivadosVolumenes);
  for (const c of PROCEDIMIENTOS_HEMORRAGIA_OPCIONES) {
    if ((e.procedimientosHemorragia[c] ?? '') === 'si') procedimientos.crear(ctx, c, t(`${I}.procedimientosHemorragia.opciones.${c}`));
  }
}

export function agregarNearMiss(ctx: Contexto051, e: EstadoNearMiss051, procedimientos: ProcedimientosCreados): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('near-miss', 'Near miss materno'));
  const abierto = e.presente !== 'no';
  const c = (clave: string, display: string, valor: string | undefined): Comp => (abierto ? cSiNoSd(local(clave, display), valor) : undefined);

  s.grupo(
    local('near-miss-criterios-clinicos', t(`${N}.presente`)),
    NEAR_MISS_CRITERIOS_CLINICOS_OPCIONES.map((k) => c(`criterio-${k}`, t(`${N}.criteriosClinicos.${k}`), e.criteriosClinicos[k])),
    e.presente === 'si' ? { valueBoolean: true } : e.presente === 'no' ? { valueBoolean: false } : {}
  );
  s.grupo(
    local('near-miss-laboratorio', 'Near miss: criterios de laboratorio'),
    NEAR_MISS_LABORATORIO_ITEMS_OPCIONES.map((k) => c(`laboratorio-${k}`, t(`${N}.laboratorio.${k}`), e.laboratorio[k]))
  );
  s.grupo(local('near-miss-intervenciones', 'Near miss: intervenciones'), [
    c('agentes-vasoactivos', t(`${N}.agentesVasoactivos`), e.agentesVasoactivos),
    abierto && e.agentesVasoactivos === 'si' ? cTexto(local('agentes-vasoactivos-cual', t(`${N}.agentesVasoactivosCual`)), e.agentesVasoactivosCual) : undefined,
    c('intubacion-ventilacion', t(`${N}.intubacionVentilacion`), e.intubacionVentilacion),
    abierto && e.intubacionVentilacion === 'si' ? cNumero(local('intubacion-ventilacion-dias', t(`${N}.intubacionVentilacionDias`)), e.intubacionVentilacionDias, U.dias) : undefined,
    ...NEAR_MISS_INTERVENCIONES_SIMPLES_OPCIONES.flatMap((k) => [
      c(`intervencion-${k}`, t(`${N}.intervencionesSimples.${k}`), e.intervencionesSimples[k]),
      abierto && k === 'ingresoUci7Dias' && e.intervencionesSimples[k] === 'si' ? cNumero(local('near-miss-ingreso-uci-dias', t(`${N}.ingresoUciDias`)), e.ingresoUciDias, U.dias) : undefined,
    ]),
  ]);
  s.cerrar();

  if (e.presente === 'si') crearCondicion(ctx, codigoMorbilidad('near-miss-materno', 'Near miss materno (morbilidad materna extrema)'));
  if (!abierto) return;
  const si = (k: string): boolean => e.intervencionesSimples[k] === 'si';
  if (si('hemoderivados3Vol')) procedimientos.crear(ctx, 'transfusion-hemoderivados', t(`${N}.intervencionesSimples.hemoderivados3Vol`));
  if (si('histerectomia')) procedimientos.crear(ctx, 'histerectomia', t(`${N}.intervencionesSimples.histerectomia`));
  if (si('dialisisIra')) procedimientos.crear(ctx, 'dialisis-ira', t(`${N}.intervencionesSimples.dialisisIra`));
  if (si('reanimacionCardiopulmonar')) procedimientos.crear(ctx, 'reanimacion-cardiopulmonar', t(`${N}.intervencionesSimples.reanimacionCardiopulmonar`));
  if (e.intubacionVentilacion === 'si') procedimientos.crear(ctx, 'intubacion-ventilacion-mecanica', t(`${N}.intubacionVentilacion`), undefined, e.intubacionVentilacionDias ? `${e.intubacionVentilacionDias.trim()} d` : undefined);
}
